import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_BASE_PATH } from './config';
import { Ad } from './models/ad.model';
import { AdvertiserProfile, UpdateProfilePayload } from './models/advertiser.model';

interface LoginResponse { message: string; data: { token: string }; code: number }
interface RegisterPayload {
  name: string; email: string; phone: string; password: string; document: string;
  postal_code: string; address_number: string; complement?: string;
}

interface UploadUrlResponse {
  message: string;
  data: {
    bucket: string;
    contentType: string;
    fileURL: string;
    key: string;
    region: string;
    uploadURL: string;
  };
  code: number;
}

interface PixQrCodeResponse {
  message: string;
  code: number;
  data: {
    EncodedImage: string;
    Payload: string;
    ExpirationDate: string;
  };
}

interface CreditCardPaymentPayload {
  payment_id: string;
  card_data: {
    number: string;
    cvv: string;
    expiration_month: string;
    expiration_year: string;
  };
  holder_data: {
    name: string;
    cpf_cnpj: string;
  };
  email: string;
}

interface StoreByEmailResponse {
  store_id: string | null;
}

interface CreateVideoResponse {
  video: {
    id: string;
    name: string;
    description: string;
    is_sponsored: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  upload_link: string;
  s3_path: string;
}

interface VideoUploadCompleteResponse {
  message: string;
  code: number;
}

export interface CreateVideoPayload {
  name: string;
  description?: string;
  file_size: number;
  width?: number;
  height?: number;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_PATH);

  login(email: string, password: string) {
    return this.http.post<LoginResponse>(`${this.base}/advertisers/login`, { email, password });
  }

  register(body: RegisterPayload) {
    return this.http.post(`${this.base}/advertisers/register`, body);
  }

  requestPasswordReset(email: string) {
    return this.http.get<{message: string; code: number}>(`${this.base}/advertisers/password-reset?email=${encodeURIComponent(email)}`);
  }

  resetPassword(email: string, code: string, password: string) {
    return this.http.post<{message: string; code: number}>(`${this.base}/advertisers/password-reset`, { email, code, password });
  }

  getAds() {
    return this.http.get<Ad[]>(`${this.base}/advertisers/ads`);
  }

  getAd(id: string | number) {
    return this.http.get<Ad>(`${this.base}/advertisers/ads/${id}`);
  }

  updateAd(id: string | number, body: Partial<{
    title: string;
    text: string;
    url_redirect: string;
    image_url: string;
    status: string;
    target: string;
    error_message: string;
    total_days: number;
    days: number;
  }>) {
    return this.http.put<Ad>(`${this.base}/advertisers/ads/${id}`, body);
  }

  requestProfileImageUploadUrl() {
    return this.http.get<UploadUrlResponse>(`${this.base}/users/profile-image-upload-url`);
  }

  createAd(body: {
    title: string;
    text: string;
    url_redirect: string;
    image_url?: string;
    video_id?: string;
    days: number;
    position: string;
    audience: string;
    target: string;
  }) {
    return this.http.post<Ad>(`${this.base}/advertisers/ads`, body);
  }

  createVideo(body: CreateVideoPayload) {
    return this.http.post<CreateVideoResponse>(`${this.base}/advertisers/videos`, body);
  }

  completeVideoUpload(videoId: string, s3Path: string) {
    return this.http.post<VideoUploadCompleteResponse>(`${this.base}/advertisers/videos/${videoId}/complete`, { s3_path: s3Path });
  }

  calculatePrice(body: { days: number; position: string; audience: string }) {
    return this.http.post<{ 
      daily_value: number; 
      view_value: number;
      total_days: number; 
      total_value: number;
      position: string;
      audience: string;
    }>(
      `${this.base}/advertisers/ads/calculate-price`, body
    );
  }

  pauseResumeAd(id: string | number) {
    return this.http.post<Ad>(`${this.base}/advertisers/ads/${id}/pause`, {});
  }

  getStoreByEmail(email: string) {
    return this.http.get<StoreByEmailResponse>(`${this.base}/auth/store-by-email`, {
      params: { email }
    });
  }

  getPixQrCode(purchaseId: string | number) {
    return this.http.post<PixQrCodeResponse>(`${this.base}/transactions/asaas/pix-qr-code`, { payment_id: purchaseId });
  }

  payWithCreditCard(body: CreditCardPaymentPayload) {
    return this.http.post(`${this.base}/transactions/asaas/pay-with-credit-card`, body);
  }

  // Profile management endpoints
  getProfile() {
    return this.http.get<AdvertiserProfile>(`${this.base}/advertisers/me`);
  }

  updateProfile(body: UpdateProfilePayload) {
    return this.http.put<AdvertiserProfile>(`${this.base}/advertisers/profile`, body);
  }
}
